/**
 * BillingPage.tsx
 *
 * Página de planes y facturación del dashboard.
 *
 * Lógica de botones por estado:
 * - Trial activo/vencido + sin Stripe → createCheckoutSession
 * - Plan Stripe activo + mismo plan → disabled "Plan Actual"
 * - Plan Stripe activo + mismo plan diferente período → createCheckoutSession (Stripe no permite cambiar interval en sub activa)
 * - Plan Stripe activo + plan mayor → changePlan (upgrade inmediato con prorrateo)
 * - Plan Stripe activo + plan menor → changePlan (aplica al vencer, muestra fecha real)
 * - Plan manual activo/vencido → createCheckoutSession
 * - Elite → mailto contacto
 */

import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Crown, Check, Clock, Zap, Star, Loader2,
  ExternalLink, AlertTriangle, ChevronDown, ChevronUp,
  CreditCard, Calendar, TrendingUp, Sparkles
} from 'lucide-react'
import { toast } from 'react-toastify'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import GradientButton from '@/components/ui/GradientButton'
import { useAuth } from '@/hooks/useAuth'
import { useRegion } from '@/hooks/useRegion'
import { useTranslation } from 'react-i18next'
import {
  getPlansWithPricing, formatPrice, getSemestralSavings,
  PLANS
} from '@/constants/pricing'

// ─────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────
interface BillingInfo {
  agent: {
    plan: string
    stripeCustomerId: string | null
    subscriptionStatus: string | null
  }
  subscription: {
    status: string
    plan: string
    current_period_end: string | null
    trial_end: string | null
    cancel_at_period_end: boolean
    stripe_subscription_id: string | null
  } | null
  paymentHistory: {
    id: string
    amount: number
    currency: string
    status: string
    description: string
    paid_at: string
    invoice_url: string | null
  }[]
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const PLAN_ORDER: Record<string, number> = {
  free: 0, starter: 1, smart: 2, pro: 3, elite: 4
}

const PLAN_ICONS: Record<string, React.ElementType> = {
  free: Clock, starter: Zap, smart: Sparkles, pro: Star, elite: Crown
}

const PLAN_DISPLAY_NAMES: Record<string, string> = {
  free: 'Prueba Gratuita', trial: 'Prueba Gratuita',
  starter: 'Starter', smart: 'Smart',
  pro: 'Pro', elite: 'Elite'
}
const STATUS_LABELS: Record<string, { key: string; color: string }> = {
  trialing:   { key: 'statusTrialing',  color: 'text-blue-400' },
  active:     { key: 'statusActive',    color: 'text-green-400' },
  cancelled:  { key: 'statusCancelled', color: 'text-red-400' },
  expired:    { key: 'statusExpired',   color: 'text-red-400' },
  past_due:   { key: 'statusPastDue',   color: 'text-orange-400' },
}

function normalizePlan(plan: string | undefined): string {
  if (!plan) return 'free'
  const p = plan.toLowerCase()
  if (p === 'trialing' || p === 'trial') return 'free'
  return p
}

function formatDate(dateStr: string | null, locale = 'es-MX'): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString(locale, {
    day: '2-digit', month: 'long', year: 'numeric'
  })
}

function formatAmount(amount: number, currency: string, locale = 'es-MX'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'MXN',
    minimumFractionDigits: 2
  }).format(amount / 100) // Stripe devuelve centavos
}

// ─────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────
const BillingPage: React.FC = () => {
  const { agent } = useAuth()
  const { region, currency, isLoading: regionLoading } = useRegion()
  const { t, i18n } = useTranslation('billing')
  const locale = i18n.language === 'en' ? 'en-US' : 'es-MX'
  const [searchParams] = useSearchParams()

  // Notificar resultado de Stripe al regresar
  useEffect(() => {
    const upgrade = searchParams.get('upgrade')
    if (upgrade === 'success') {
      toast.success(t('toastPaySuccess'))
    } else if (upgrade === 'cancelled') {
      toast.info(t('toastPayCancelled'))
    }
  }, [])

  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'semestral'>('monthly')
  const [loading, setLoading] = useState<string | null>(null)
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null)
  const [billingLoading, setBillingLoading] = useState(true)
  const [showHistory, setShowHistory] = useState(false)

  const plans = getPlansWithPricing(region)
  const currentPlanNormalized = normalizePlan(agent?.plan)

  // ── Cargar billing info ──
  useEffect(() => {
    const fetchBillingInfo = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) return
        const res = await fetch('/api/billing/info', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          setBillingInfo(data)
        }
      } catch (e) {
        console.error('Error fetching billing info:', e)
      } finally {
        setBillingLoading(false)
      }
    }
    fetchBillingInfo()
  }, [])

  // ── Fecha de vencimiento ──
  const expiresAt = billingInfo?.subscription?.status === 'trialing'
    ? billingInfo.subscription.trial_end
    : billingInfo?.subscription?.current_period_end

  const hasStripeSubscription = !!billingInfo?.subscription?.stripe_subscription_id
  const hasStripeCustomer     = !!billingInfo?.agent?.stripeCustomerId
  const isCancelPending       = hasStripeSubscription && billingInfo?.subscription?.cancel_at_period_end

  // ── Determinar acción del botón por plan ──
  const getPlanAction = (planId: string, stripePlanId: string): {
    label: string
    disabled: boolean
    action: 'checkout' | 'change' | 'contact' | 'none'
    variant: 'primary' | 'secondary'
    tooltip?: string
  } => {
    // Elite siempre contactar
    if (planId === 'elite') {
      return { label: t('actionContactSales'), disabled: false, action: 'contact', variant: 'secondary' }
    }

    // Free nunca se "compra"
    if (planId === 'free') {
      return { label: t('actionTrialPlan'), disabled: true, action: 'none', variant: 'secondary' }
    }

    const planBase = planId
    const currentBase = currentPlanNormalized

    // Mismo plan base → Plan Actual
    if (planBase === currentBase) {
      // Si el período es diferente → permitir cambio via nuevo checkout
      const currentPeriodIsSemestral = billingInfo?.subscription?.stripe_subscription_id
        ?.includes('_6m') // heurística — Stripe no expone el interval directamente
      return {
        label: t('actionCurrentPlan'),
        disabled: true,
        action: 'none',
        variant: 'secondary',
        tooltip: t('tooltipCurrentPlan')
      }
    }

    const newOrder = PLAN_ORDER[planBase] ?? -1
    const curOrder = PLAN_ORDER[currentBase] ?? 0

    // Sin suscripción Stripe activa → siempre checkout
    if (!hasStripeSubscription) {
      return {
        label: t('actionSubscribe'),
        disabled: false,
        action: 'checkout',
        variant: newOrder > curOrder ? 'primary' : 'secondary'
      }
    }

    // Con Stripe activo
    if (newOrder > curOrder) {
      return {
        label: t('actionUpgrade'),
        disabled: false,
        action: 'change',
        variant: 'primary',
        tooltip: t('tooltipProrated')
      }
    }

    if (newOrder < curOrder) {
      return {
        label: t('actionChangePlan'),
        disabled: false,
        action: 'change',
        variant: 'secondary',
        tooltip: expiresAt
          ? t('tooltipAppliesOn', { date: formatDate(expiresAt, locale) })
          : t('tooltipAppliesEnd')
      }
    }

    return { label: t('actionSelect'), disabled: false, action: 'checkout', variant: 'secondary' }
  }

  // ── Handler principal de selección ──
  const handlePlanSelection = async (
    planId: string,
    stripePlanId: string,
    action: 'checkout' | 'change' | 'contact' | 'none'
  ) => {
    if (action === 'none') return

    if (action === 'contact') {
      window.open('mailto:ventas@innovagent.tech?subject=Solicitud Plan Elite InnovAgent', '_blank')
      toast.info(t('toastEliteContact'))
      return
    }

    setLoading(planId)
    const token = localStorage.getItem('token')
    if (!token) { toast.error(t('toastAuthError')); setLoading(null); return }

    try {
      if (action === 'change' && hasStripeSubscription) {
        // Upgrade/downgrade en suscripción existente
        const res = await fetch('/api/billing/change-plan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ planId: stripePlanId })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || t('toastChangePlanError'))

        toast.success(data.message || t('toastPlanUpdated'))

        // Recargar billing info
        const infoRes = await fetch('/api/billing/info', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (infoRes.ok) setBillingInfo(await infoRes.json())

      } else {
        // Nuevo checkout (trial, manual, o cambio de período)
        const res = await fetch('/api/billing/create-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ planId: stripePlanId })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || t('toastPaymentError'))
        if (data.checkoutUrl) window.location.href = data.checkoutUrl
        else throw new Error(t('toastNoSession'))
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toastPlanError'))
    } finally {
      setLoading(null)
    }
  }

  // ── Handler portal de Stripe ──
  const handleOpenPortal = async () => {
    const token = localStorage.getItem('token')
    if (!token) return
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
      const data = await res.json()
      if (data.portalUrl) window.open(data.portalUrl, '_blank')
      else throw new Error(t('toastNoPortal'))
    } catch {
      toast.error(t('toastPortalError'))
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  if (regionLoading) {
    return (
      <DashboardLayout activeSection="billing">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout activeSection="billing">
      <div className="space-y-6">

        {/* ── Header ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold text-white mb-1">{t('title')}</h1>
          <p className="text-white/60">{t('subtitle')}</p>
        </motion.div>

        {/* ── Card: Plan Actual ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <GlassCard>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-500/20">
                  <Crown className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {['free', 'trial'].includes(agent?.plan?.toLowerCase() || 'free') ? t('planFreeName') : (PLAN_DISPLAY_NAMES[agent?.plan?.toLowerCase() || 'free'] || agent?.plan)}
                  </h3>
                  {billingLoading ? (
                    <div className="h-4 w-32 bg-white/10 rounded animate-pulse mt-1" />
                  ) : (
                    <div className="flex flex-col gap-0.5 mt-1">
                      {/* Estado */}
                      {billingInfo?.subscription?.status && (
                        <p className={`text-sm font-medium ${STATUS_LABELS[billingInfo.subscription.status]?.color || 'text-white/60'}`}>
                          {STATUS_LABELS[billingInfo.subscription.status]?.key ? t(STATUS_LABELS[billingInfo.subscription.status].key) : billingInfo.subscription.status}
                        </p>
                      )}
                      {/* Fecha de vencimiento */}
                      {expiresAt && (
                        <p className="text-white/50 text-sm flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {billingInfo?.subscription?.status === 'trialing'
                            ? t('trialUntil', { date: formatDate(expiresAt, locale) })
                            : t('renewsOn', { date: formatDate(expiresAt, locale) })
                          }
                        </p>
                      )}
                      {/* Aviso cancelación pendiente */}
                      {isCancelPending && expiresAt && (
                        <p className="text-orange-400 text-sm flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {t('cancelsOn', { date: formatDate(expiresAt, locale) })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="px-2 py-1 rounded bg-white/10 text-white/50 text-xs">
                  {t('pricesIn', { currency })}
                </span>
                {hasStripeSubscription && (
                  <button
                    onClick={handleOpenPortal}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white/70 hover:text-white text-sm transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    {t('billingPortal')}
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* ── Toggle mensual / semestral ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex items-center justify-center gap-3"
        >
          <div className="flex items-center gap-1 p-1 bg-white/5 rounded-full border border-white/10">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-white/15 text-white border border-white/20'
                  : 'text-white/50 hover:text-white/70'
              }`}
            >
              {t('monthly')}
            </button>
            <button
              onClick={() => setBillingPeriod('semestral')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === 'semestral'
                  ? 'bg-white/15 text-white border border-white/20'
                  : 'text-white/50 hover:text-white/70'
              }`}
            >
              {t('semestral')}
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
                {t('saveUpTo')}
              </span>
            </button>
          </div>
        </motion.div>

        {/* ── Grid de planes ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4"
        >
          {plans.map((plan, index) => {
            const IconComponent = PLAN_ICONS[plan.id] || Zap
            const isCurrentPlan = normalizePlan(agent?.plan) === plan.id ||
              (agent?.plan === 'trial' && plan.id === 'free')

            // Precio según período seleccionado
            const showSemestral = billingPeriod === 'semestral' && plan.pricing.semestralPrice !== undefined
            const displayPrice  = showSemestral ? plan.pricing.semestralPrice  : plan.pricing.price
            const displayPlanId = showSemestral && plan.pricing.semestralPlanId
              ? plan.pricing.semestralPlanId
              : plan.pricing.planId
            const displayPeriod = showSemestral && plan.pricing.semestralPeriod
              ? plan.pricing.semestralPeriod
              : plan.pricing.period

            const savings = showSemestral ? getSemestralSavings(plan.id, region) : null
            const { label, disabled, action, variant, tooltip } = getPlanAction(plan.id, displayPlanId)

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                title={tooltip}
              >
                <GlassCard className={`relative h-full flex flex-col ${
                  plan.popular ? 'ring-2 ring-blue-500' : ''
                } ${isCurrentPlan ? 'ring-1 ring-white/20' : ''}`}>

                  {plan.popular && (
                    <div className="absolute top-0 right-0 overflow-hidden w-20 h-20 z-10">
                      <div className="absolute top-3 -right-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-bold py-1 w-24 text-center shadow-md"
                        style={{ transform: 'rotate(45deg)' }}>
                          {t('popular')}
                      </div>
                    </div>
                  )}
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <span className="bg-white/20 text-white px-4 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-white/30">
                        {t('currentPlanBadge')}
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-4">
                    <div className="mx-auto w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mb-3">
                      <IconComponent className="h-5 w-5 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                    <p className="text-white/40 text-xs mb-3">{plan.description}</p>

                    <div className="flex flex-col items-center">
                      {plan.elite ? (
                        <span className="text-xl font-bold text-amber-400">{t('contact')}</span>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold text-white">
                              {formatPrice(displayPrice ?? null, plan.pricing.currency)}
                            </span>
                            {displayPrice !== null && displayPrice !== undefined && displayPrice > 0 && (
                              <span className="text-white/50 text-xs">
                                /{displayPeriod}
                              </span>
                            )}
                            {displayPrice === 0 && (
                              <span className="text-white/50 text-xs">{displayPeriod}</span>
                            )}
                          </div>
                          {savings && savings > 0 && (
                            <span className="text-green-400 text-xs mt-1 flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              {t('savePerMonth', { amount: formatPrice(savings, plan.pricing.currency) })}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-2 mb-4 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-white/70">
                        <Check className="h-3.5 w-3.5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span className={`text-xs ${
                          feature.startsWith('Todo lo') ? 'italic text-white/40' : ''
                        }`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <GradientButton
                    className="w-full mt-auto text-sm py-2"
                    disabled={loading !== null || disabled}
                    variant={variant}
                    onClick={() => handlePlanSelection(plan.id, displayPlanId, action)}
                  >
                    {loading === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {t('processing')}
                      </span>
                    ) : label}
                  </GradientButton>
                </GlassCard>
              </motion.div>
            )
          })}
        </motion.div>

        {/* ── Nota de facturación ── */}
        <p className="text-center text-white/40 text-xs">
          {currency === 'MXN'
            ? t('noticeMXN')
            : t('noticeUSD')
          }
        </p>

        {/* ── Historial de pagos ── */}
        {!billingLoading && billingInfo?.paymentHistory && billingInfo.paymentHistory.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <GlassCard>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center justify-between w-full"
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-blue-400" />
                  <h3 className="text-white font-semibold">
                    {t('paymentHistory')}
                    <span className="ml-2 text-white/40 text-sm font-normal">
                      {t('recordsCount', { count: billingInfo.paymentHistory.length })}
                    </span>
                  </h3>
                </div>
                {showHistory
                  ? <ChevronUp className="h-4 w-4 text-white/40" />
                  : <ChevronDown className="h-4 w-4 text-white/40" />
                }
              </button>

              {showHistory && (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white/40 text-xs border-b border-white/10">
                        <th className="text-left pb-2 font-medium">{t('colDate')}</th>
                        <th className="text-left pb-2 font-medium">{t('colDescription')}</th>
                        <th className="text-right pb-2 font-medium">{t('colAmount')}</th>
                        <th className="text-right pb-2 font-medium">{t('colStatus')}</th>
                        <th className="text-right pb-2 font-medium">{t('colReceipt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingInfo.paymentHistory.map(payment => (
                        <tr key={payment.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 text-white/60 text-xs">
                            {formatDate(payment.paid_at, locale)}
                          </td>
                          <td className="py-3 text-white/80 text-xs pr-4">
                            {payment.description || t('defaultDescription')}
                          </td>
                          <td className="py-3 text-white font-medium text-right">
                            {formatAmount(payment.amount, payment.currency, locale)}
                          </td>
                          <td className="py-3 text-right">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              payment.status === 'succeeded'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {payment.status === 'succeeded' ? t('statusPaid') : t('statusFailed')}
                            </span>
                          </td>
                          <td className="py-3 text-right">
                            {payment.invoice_url && (
                              <a
                                href={payment.invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 justify-end"
                              >
                                {t('viewReceipt')} <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </GlassCard>
          </motion.div>
        )}

        {/* Mensaje si no hay historial */}
        {!billingLoading && (!billingInfo?.paymentHistory || billingInfo.paymentHistory.length === 0) && (
          <p className="text-center text-white/30 text-xs">
            {t('noHistory')}
          </p>
        )}

      </div>
    </DashboardLayout>
  )
}

export default BillingPage
